<template>
    <div class="container-fluid">
        <!-- Permission denied banner -->
        <div v-if="!hasAccess" class="alert alert-warning d-flex align-items-center gap-2 mb-3">
            <i class="material-icons">lock</i>
            <span>{{ denyReason || 'You do not have permission to modify admin groups.' }}</span>
        </div>
        <!-- Organization/location roles are scoped to the current Edit User location. -->
        <div v-if="canModifyAdminGroups" class="card mb-4">
            <div class="card-header bg-info text-white d-flex align-items-center justify-content-between">
                <h6 class="mb-0 text-white">
                    <i class="material-icons align-middle me-2">admin_panel_settings</i>
                    Organization and Location Admin Permissions
                </h6>
                <span v-if="roleLoading" class="spinner-border spinner-border-sm" role="status"
                    aria-label="Loading admin roles"></span>
            </div>
            <div class="card-body">
                <div v-if="roleError" class="alert alert-warning mb-0">{{ roleError }}</div>
                <template v-else-if="roleOptions">
                    <div v-if="roleOptions.organization" class="form-check form-switch mb-3">
                        <input id="organization-admin-role" v-model="organizationAdmin" class="form-check-input"
                            type="checkbox" :disabled="roleSaving || !canGrantOrganizationAdmin" />
                        <label class="form-check-label" for="organization-admin-role">
                            Organization Admin — {{ roleOptions.organization.name }}
                        </label>
                        <small v-if="!canGrantOrganizationAdmin" class="d-block text-muted">
                            <i class="material-icons align-middle text-sm">lock</i>
                            You do not have permission to manage organization admin roles.
                        </small>
                    </div>

                    <div v-if="roleOptions.location" class="form-check form-switch mb-3">
                        <input id="location-admin-role" v-model="locationAdmin" class="form-check-input"
                            type="checkbox" :disabled="roleSaving || !canGrantLocationAdmin" />
                        <label class="form-check-label" for="location-admin-role">
                            Location Admin — {{ roleOptions.location.name }}
                        </label>
                        <small v-if="roleOptions.location.effective && !locationAdmin" class="d-block text-muted">
                            Organization admin access currently grants inherited location access.
                        </small>
                        <small v-if="!canGrantLocationAdmin" class="d-block text-muted">
                            <i class="material-icons align-middle text-sm">lock</i>
                            You do not have permission to manage location admin roles.
                        </small>
                    </div>

                    <div class="d-flex align-items-center gap-2">
                        <button class="btn btn-info btn-sm" type="button" :disabled="!roleChanges || roleSaving"
                            @click="handleSaveAdminRoles">
                            <span v-if="roleSaving" class="spinner-border spinner-border-sm me-2" role="status"
                                aria-hidden="true"></span>
                            <i v-else class="material-icons text-lg me-1">save</i>
                            {{ roleSaving ? 'Saving...' : 'Save Admin Permissions' }}
                        </button>
                        <small v-if="!canGrantOrganizationAdmin && !canGrantLocationAdmin" class="text-muted">
                            These permissions are readonly for your admin level.
                        </small>
                    </div>
                </template>
            </div>
        </div>

        <!-- Activities: hidden when user cannot assign any -->
        <div v-if="filteredAvailableActivities.length > 0 || userActivities.length > 0">
            <TagSelector optionName="users" availableOptionsHeading="Activities and Events List"
                selectedOptionsHeading="Admin activities and events" :initial-skills="filteredAvailableActivities"
                v-model:interests="userActivities" :on-save="hasAccess ? handleSaveActivities : null" :loading="loading"
                :isSaving="activitiessubmitting" :disabled="!hasAccess" />
        </div>

        <div class="mt-4" v-if="filteredAvailableWorkgroups.length > 0 || userWorkgroups.length > 0">
            <TagSelector optionName="users" availableOptionsHeading="Workgroups List"
                selectedOptionsHeading="Admin Workgroups" :initial-skills="filteredAvailableWorkgroups"
                v-model:interests="userWorkgroups" :on-save="hasAccess ? handleSaveWorkgroups : null" :loading="loading"
                :isSaving="workgroupssubmitting" :disabled="!hasAccess" />
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, inject } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import TagSelector from "@/components/common/TagSelector.vue";
import { getLocationId } from "../../../utils/SessionDataHandler";
import { usePermissions } from "@/composables/usePermissions";

const props = defineProps({
    targetUserInfo: { type: Object, default: null },
});

const loading = ref(false);
const activitiessubmitting = ref(false);
const workgroupssubmitting = ref(false);

// top-level composables
const store = useStore();
const route = useRoute();
const {
    canModifyAdminGroups,
    isSuperAdmin,
    isOrganizationAdmin,
    isLocationAdmin,
    canAccessUserBySkill,
    getFilteredAdminActivities,
    getFilteredAdminWorkgroups,
} = usePermissions();

const _injectedPersonId = inject('editUserPersonId', null);
const personId = _injectedPersonId?.value || Number(route.params.userid);
const locationId = getLocationId();

// Combined access: must have admin-group permission AND skill-based access
const skillAccess = computed(() => {
    if (!props.targetUserInfo) return { canAccess: true, reason: "" };
    return canAccessUserBySkill(props.targetUserInfo);
});
const hasAccess = computed(() => canModifyAdminGroups.value && skillAccess.value.canAccess);
const denyReason = computed(() => {
    if (!canModifyAdminGroups.value) return "You do not have permission to modify admin groups.";
    return skillAccess.value.reason;
});

// state
const availableActivities = ref([]);
const userActivities = ref([]);
const availableWorkgroups = ref([]);
const userWorkgroups = ref([]);

// Out-of-scope IDs to preserve on save
const outOfScopeActivityIds = ref([]);
const outOfScopeWorkgroupIds = ref([]);
const roleOptions = ref(null);
const roleLoading = ref(false);
const roleSaving = ref(false);
const roleError = ref("");
const organizationAdmin = ref(false);
const locationAdmin = ref(false);

const canGrantOrganizationAdmin = computed(() =>
    roleOptions.value?.can_grant_organization_admin ??
    (isSuperAdmin.value || isOrganizationAdmin.value)
);
const canGrantLocationAdmin = computed(() =>
    roleOptions.value?.can_grant_location_admin ??
    (isSuperAdmin.value || isLocationAdmin.value)
);
const roleChanges = computed(() => {
    if (!roleOptions.value) return false;
    const organizationChanged = roleOptions.value.organization
        ? organizationAdmin.value !== !!roleOptions.value.organization.assigned
        : false;
    const locationChanged = roleOptions.value.location
        ? locationAdmin.value !== !!roleOptions.value.location.assigned
        : false;
    return organizationChanged || locationChanged;
});

// Permission-filtered lists
const filteredAvailableActivities = computed(() =>
    getFilteredAdminActivities(availableActivities.value)
);
const filteredAvailableWorkgroups = computed(() =>
    getFilteredAdminWorkgroups(availableWorkgroups.value)
);

// mappers
const mapActivities = (list) =>
    list.map((a) => ({
        id: a.activityid,
        name: a.activityname,
        description: a.description ?? "",
    }));

const mapWorkgroups = (list) =>
    list.map((w) => ({
        id: w.workgroupid,
        name: w.displayText,
        description: w.description ?? "",
    }));

// initial fetch
onMounted(async () => {
    loading.value = true;
    try {
        roleLoading.value = true;
        try {
            const rolesRes = await store.dispatch("fetchAdminRoleOptions", {
                locationId,
                personId,
            });
            roleOptions.value = rolesRes;
            organizationAdmin.value = !!rolesRes.organization?.assigned;
            locationAdmin.value = !!rolesRes.location?.assigned;
        } catch (error) {
            roleError.value = "Admin role permissions could not be loaded.";
        } finally {
            roleLoading.value = false;
        }
        const activitiesRes = await store.dispatch("fetchActivities", {
            locationId,
            personId,
        });
        availableActivities.value = mapActivities(activitiesRes.activities_list);
        const allUserActivities = mapActivities(activitiesRes.admin_activities);

        const workgroupsRes = await store.dispatch("fetchAdminWorkgroups", {
            locationId,
            personId,
        });
        availableWorkgroups.value = mapWorkgroups(workgroupsRes.workgroup_list);
        const allUserWorkgroups = mapWorkgroups(workgroupsRes.admin_work_groups);

        // Split user's existing admin groups into in-scope (visible) and
        // out-of-scope (preserved silently on save).
        const filtActIds = new Set(filteredAvailableActivities.value.map((a) => Number(a.id)));
        userActivities.value = allUserActivities.filter((a) => filtActIds.has(Number(a.id)));
        outOfScopeActivityIds.value = allUserActivities
            .filter((a) => !filtActIds.has(Number(a.id)))
            .map((a) => a.id);

        const filtWgIds = new Set(filteredAvailableWorkgroups.value.map((w) => Number(w.id)));
        userWorkgroups.value = allUserWorkgroups.filter((w) => filtWgIds.has(Number(w.id)));
        outOfScopeWorkgroupIds.value = allUserWorkgroups
            .filter((w) => !filtWgIds.has(Number(w.id)))
            .map((w) => w.id);
    } finally {
        loading.value = false;
    }
});

const handleSaveAdminRoles = async () => {
    if (!roleOptions.value || roleSaving.value || !roleChanges.value) return;
    if (!canGrantOrganizationAdmin.value && !canGrantLocationAdmin.value) return;

    roleSaving.value = true;
    try {
        const rolePayload = {
            personId,
            locationId,
        };
        if (canGrantOrganizationAdmin.value) {
            rolePayload.organizationAdmin = organizationAdmin.value;
        }
        if (canGrantLocationAdmin.value) {
            rolePayload.locationAdmin = locationAdmin.value;
        }
        await store.dispatch("saveAdminRoles", rolePayload);
        const refreshed = await store.dispatch("fetchAdminRoleOptions", {
            locationId,
            personId,
        });
        roleOptions.value = refreshed;
        organizationAdmin.value = !!refreshed.organization?.assigned;
        locationAdmin.value = !!refreshed.location?.assigned;
    } finally {
        roleSaving.value = false;
    }
};
// promise-returning save handlers — merge out-of-scope IDs on save
const handleSaveActivities = async (selected) => {
    if (!hasAccess.value) return;
    activitiessubmitting.value = true;
    try {
        const selectedIds = selected.map((x) => x.id);
        const merged = [...selectedIds, ...outOfScopeActivityIds.value];
        await store.dispatch("saveAdminActivities", {
            personId,
            locationId,
            activitiesArray: merged,
        });
    }
    finally {
        activitiessubmitting.value = false;
    }
};

const handleSaveWorkgroups = async (selected) => {
    if (!hasAccess.value) return;
    workgroupssubmitting.value = true;
    try {
        const selectedIds = selected.map((x) => x.id);
        const merged = [...selectedIds, ...outOfScopeWorkgroupIds.value];
        await store.dispatch("saveAdminWorkgroups", {
            personId,
            locationId,
            workgroupsArray: merged,
        });
    }
    finally {
        workgroupssubmitting.value = false;
    }
};
</script>
